"use client";

import React, { useState, useEffect, useRef } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useParams } from "next/navigation";
import { Statistics, Author, AnalysisMeta } from "@/types/types";
import { useTranslations } from "next-intl";
import { FaSearch } from "react-icons/fa";
import { IoClose } from "react-icons/io5";

import Header from "@/components/header";
import Footer from "@/components/footer";
import Loader from "@/components/loading";
import Skeleton from "./skeleton";

import AuthorSearch from "./AuthorSearch";
import StatisticsDisplay from "./StatisticsDisplay";
import ChartDisplay from "./ChartDisplay";
import PublicationsList from "./PublicationsList";
import ExportButton from "./ExportButton";

import { analyze } from "./mtmtHelpers";
import fetch from "@/components/fetch";

// Styles
import "react-toastify/dist/ReactToastify.css";
import "@/app/loadingv2.css";

export default function MTMTStatistics() {
  const params = useParams<{ mtid: string }>();
  const t = useTranslations("core");
  const headerT = useTranslations("header");
  const statsT = useTranslations('statistics');
  const typeT = useTranslations("statistics.publicationTypes");
  const [isLoading, setIsLoading] = useState(true);
  const [author, setAuthor] = useState<Author>({
    mtid: params.mtid,
    name: "",
    degrees: [],
    identifiers: [],
  });
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [chartData, setChartData] = useState<any>({
    publicationsWithRankings: null,
    cumulativePublications: null,
    iScore: null,
    cumulativeIScore: null,
    qScore: null,
    cumulativeQScore: null,
  });
  const [publications, setPublications] = useState<any>({
    data: [],
    authors: [],
    types: [],
    journals: [],
  });
  const [meta, setMeta] = useState<AnalysisMeta | null>(null);
  const [excludeNorwayLevel0, setExcludeNorwayLevel0] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('excludeNorwayLevel0');
      return saved === 'true';
    }
    return false;
  });
  const [showAuthorSearchModal, setShowAuthorSearchModal] = useState(false);

  const statisticsRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const homepageUrl = author.identifiers?.find(
    (id: any) => id?.source?.label === "Homepage" || id?.source?.label === "Honlap"
  )?.realUrl;
  const authorLinkUrl = homepageUrl || `https://m2.mtmt.hu/api/author/${author.mtid}`;

  const publicationTypeTranslations = {
    journalArticle: typeT("journalArticle"),
    conferenceArticle: typeT("conferenceArticle"),
    bookChapter: typeT("bookChapter"),
    book: typeT("book"),
    patent: typeT("patent"),
    thesis: typeT("thesis"),
    other: typeT("other")
  };

  useEffect(() => {
    if (!author.name) {
      checkMTMTID(params.mtid);
    }
  }, [params.mtid]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        buttonRef.current &&
        !modalRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowAuthorSearchModal(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const checkMTMTID = async (id: string) => {
    if (!id) {
      toast.error(t("checkError"));
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch.author(id);
      
      if (!response.ok) {
        toast.error(t("checkError"));
        setIsLoading(false);
        return null;
      }

      setAuthor((prev) => ({
        ...prev,
        isValid: response.ok,
        name: `${response.data.content.familyName} ${response.data.content.givenName} (${response.data.content.auxName})`,
        degrees: response.data.content.degrees,
        identifiers: response.data.content.identifiers,
      }));

      handleFetch(id);
    } catch (error) {
      toast.error(t("checkError") || "Network error occurred. Reloading page...");
      // Reload page after 2 seconds
      setTimeout(() => {
        window.location.reload();
      }, 2000);
      return null;
    }
  };

  const handleFetch = async (id: string) => {
    setIsLoading(true);
    const res = await fetch.publications(id);
    if (!res.ok) {
      toast.error(t("error"));
      return;
    }

    const pubs = res.data?.content || [];

    const { statistics, chartData, authors, types, journals, publications, meta } =
      await analyze(author, pubs, publicationTypeTranslations, excludeNorwayLevel0);

    setPublications({
      data: publications,
      authors: authors,
      types: types,
      journals: journals,
    });
    setStatistics(statistics);
    setChartData(chartData);
    setMeta(meta);
    setIsLoading(false);
  };

  const handleToggleExcludeNorwayLevel0 = async (enabled: boolean) => {
    setExcludeNorwayLevel0(enabled);
    localStorage.setItem('excludeNorwayLevel0', enabled.toString());
    
    // Re-analyze with new setting
    if (author.mtid) {
      setIsLoading(true);
      const res = await fetch.publications(author.mtid);
      if (res.ok) {
        const pubs = res.data?.content || [];
        const { statistics, chartData, authors, types, journals, publications, meta } =
          await analyze(author, pubs, publicationTypeTranslations, enabled);
        
        setPublications({
          data: publications,
          authors: authors,
          types: types,
          journals: journals,
        });
        setStatistics(statistics);
        setChartData(chartData);
        setMeta(meta);
      }
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="relative">
        <Skeleton mtid={params.mtid} />
        <div className="fixed inset-0 w-full h-full flex items-center justify-center z-50 bg-black/10 backdrop-blur-sm">
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-linear-to-br from-gray-50 to-gray-100">
      <Header name={t("title")} desc={headerT("description")} />

      {/* Hero */}
      <section className="relative bg-linear-to-br from-gray-900 to-black text-white">
        {/* Hanging Author Search Button */}
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 z-10">
          <button
            ref={buttonRef}
            onClick={() => setShowAuthorSearchModal(!showAuthorSearchModal)}
            className="flex items-center justify-center gap-2 px-3.5 py-1.5 bg-neutral-800 hover:bg-neutral-700 text-gray-100 hover:text-white rounded-t-none rounded-b-2xl border border-neutral-700 border-t-0 shadow-lg transition-colors hover:scale-105 cursor-pointer text-xs sm:text-sm font-medium backdrop-blur-md"
            aria-label="Search for author"
          >
            <FaSearch className="w-4 h-4 text-gray-400" />
            <span>{t("searchAuthor") || "Search Author"}</span>
          </button>
        </div>

        <div className="container mx-auto max-w-7xl px-4 py-10 sm:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-2">
              <h1 className="text-2xl sm:text-4xl font-bold leading-tight wrap-break-words">
                <a
                  href={authorLinkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline decoration-bme-main/40 transition-colors"
                >
                  {author?.name || t("title")}
                </a>
              </h1>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                {author.degrees && author.degrees.length > 0 && (
                  author.degrees.map((degree: any, index: number) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-white/10 rounded-full text-xs sm:text-sm text-white/90"
                    >
                      {degree.label}
                    </span>
                  ))
                )}
                <a
                  href={`https://m2.mtmt.hu/api/author/${author.mtid}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1 bg-white/10 rounded-full text-xs sm:text-sm text-white/90 hover:text-bme-main transition-colors"
                >
                  MTMT ID: {author.mtid}
                </a>
              </div>
            </div>
            <div className="rounded-2xl bg-white/10 border border-white/10 backdrop-blur p-5">
              <div className="flex flex-col gap-1.5 text-sm">
                <div className="text-white/90">
                  {statsT('lastUpdate')}: <span className="text-white">{meta?.lastUpdated || '-'}</span>
                </div>
                {meta?.ifListVersionYear != null && (
                  <div className="text-white/90">
                    {statsT('datasets.ifList')}: <span className="text-white">{meta.ifListVersionYear}</span>
                  </div>
                )}
                {meta?.norwayListVersionYear != null && (
                  <div className="text-white/90">
                    {statsT('datasets.norwayList')}: <span className="text-white">{meta.norwayListVersionYear}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-linear-to-r from-transparent via-bme-main to-transparent opacity-80" />
      </section>

      <main className="grow container mx-auto px-4 py-1 max-w-7xl">
        <div className="flex flex-row items-center justify-between gap-3 sm:gap-4 mt-3 sm:mt-2 mb-4 sm:mb-4">
          <div className="flex items-center gap-3 sm:gap-2 min-w-0 flex-1">
            <label className="flex items-center gap-3 sm:gap-2 cursor-pointer min-w-0">
              <input
                type="checkbox"
                checked={excludeNorwayLevel0}
                onChange={(e) => handleToggleExcludeNorwayLevel0(e.target.checked)}
                className="w-5 h-5 sm:w-4 sm:h-4 text-bme-main border-gray-300 rounded focus:ring-bme-main focus:ring-2 shrink-0"
              />
              <span className="text-base sm:text-sm font-medium text-gray-700 leading-tight">
                {statsT("excludeNorwayLevel0")}
              </span>
            </label>
          </div>
          <ExportButton
            author={author}
            stats={statistics}
            publications={publications.data}
            meta={meta as AnalysisMeta}
          />
        </div>

        <div ref={statisticsRef}>
          <StatisticsDisplay
            statistics={statistics}
          />
        </div>

        <ChartDisplay chartData={chartData} />

        <PublicationsList
          publications={publications.data}
          authors={publications.authors}
          types={publications.types}
          journals={publications.journals}
        />
      </main>

      {/* Author Search Modal */}
      {showAuthorSearchModal && (
        <>
          {/* Background Overlay */}
          <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm" />
          
          {/* Modal */}
          <div
            ref={modalRef}
            className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 w-[90vw] max-w-2xl bg-white rounded-2xl shadow-2xl border border-gray-200"
            role="dialog"
            aria-modal="true"
            aria-labelledby="author-search-modal-title"
          >
            <div className="px-4 sm:px-6 py-4 flex items-center justify-between">
              <h2 id="author-search-modal-title" className="text-lg sm:text-xl font-semibold text-gray-800">
                {t("searchAuthor") || "Search Author"}
              </h2>
              <button
                onClick={() => setShowAuthorSearchModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
                aria-label="Close modal"
              >
                <IoClose className="w-5 h-5" />
              </button>
            </div>
            <div className="px-4 sm:px-6 pb-4 sm:pb-6">
              <AuthorSearch />
            </div>
          </div>
        </>
      )}

      <ToastContainer />
      <Footer />
    </div>
  );
}
